import React from 'react'
import styled from 'styled-components'
import { Field } from 'redux-form'
import pluralize from 'pluralize'

import ChevronDown from '../icons/ChevronDown'
import StepperButtonMinusIcon from '../icons/StepperButtonMinus'
import StepperButtonPlusIcon from '../icons/StepperButtonPlus'
import Panel from '../display/Panel'

import { AGE_ANNOTATIONS } from '../../search/filters/common/QuantitySelect'

export const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 18px;

  ${({ noMargin }) => !noMargin && 'margin: 8px 0 8px;'}

  border: 1px solid
    ${({ theme, error }) =>
      error
        ? theme?.['-ds-error-300']?.value
        : theme?.['-ds-neutral-400']?.value};

  ${({ rounded }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  ${({ open }) =>
    open &&
    `
    border-bottom: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}

  background-color: white;

  &:focus {
    border-color: ${({ theme }) => theme.brand.jungle};
  }

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    pointer-events: none;
    top: 0;
    right: 0;

    height: 100%;
    padding: 0 18px;

    color: ${({ theme }) => theme?.['-ds-neutral-400']?.value};
  }

  svg {
    width: 13px;
    height: 6px;
  }
`

const Trigger = styled.button.attrs({ type: 'button' })`
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 18px;

  text-align: left;
  appearance: none;
  background: transparent;
  border: 0;
  outline: 0;
`

const Dropdown = styled.div`
  position: absolute;
  left: -1px;
  right: -1px;
  top: 100%;

  padding: 18px;

  border: 1px solid ${({ theme }) => theme?.['-ds-neutral-400']?.value};
  border-top: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  background-color: white;

  z-index: 9;
`

export const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 15px;
`

export const Label = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  div {
    color: ${({ theme }) => theme.colors.labelGrey};
    font-size: 14px;
  }
`

export const Footer = styled.footer`
  width: 100%;
  margin-top: 20px;
  text-align: right;
`

export const CloseButton = styled.button`
  display: inline-block;
  appearance: none;
  outline: 0;
  border: none;
  background: none;

  padding: 10px 20px;
  padding-right: 0;
  color: ${({ theme }) => theme.brand.jungle};

  text-transform: uppercase;
`

const Stepper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100px;
`

const StepButton = styled.button.attrs({ type: 'button' })`
  width: 26px;
  height: 26px;
  padding: 0;

  appearance: none;
  border: none;
  background: none;
  border-radius: 0;

  text-align: center;

  color: ${({ theme, active }) => (active ? theme.brand.jungle : '#C9C9C9')};

  svg {
    display: inline-block;
    width: 100%;
    height: 100%;
  }
`

export const renderStepper = ({
  input: { value, onChange },
  guests,
  capacity,
  minValue = 0,
  increaseLabel = 'Plus',
  decreaseLabel = 'Minus',
}) => (
  <Stepper>
    <StepButton
      onClick={() => onChange(value - 1 >= minValue ? value - 1 : value)}
      active={value > minValue}
      aria-label={decreaseLabel}
    >
      <StepperButtonMinusIcon />
    </StepButton>
    <div>{value}</div>
    <StepButton
      onClick={() => onChange(guests < capacity ? value + 1 : value)}
      active={guests < capacity}
      aria-label={increaseLabel}
    >
      <StepperButtonPlusIcon />
    </StepButton>
  </Stepper>
)

class GuestsInput extends React.Component {
  state = {
    open: false,
  }

  handleToggle = () => {
    this.setState({ open: !this.state.open })
  }

  handleClose = () => {
    this.setState({ open: false })
  }

  render() {
    const { guests } = this.props
    const { open } = this.state

    return (
      <Wrapper className="js-guests-input field" open={open} {...this.props}>
        &nbsp;
        <Trigger onClick={this.handleToggle}>
          {pluralize('Person', guests, true)}
        </Trigger>
        <Panel id="guests-input" open={open} onClose={this.handleClose}>
          <Dropdown>
            <Option aria-label="Adults">
              <Label>
                Adults
                <div>{AGE_ANNOTATIONS.ADULT}</div>
              </Label>
              <div>
                <Field
                  name="booking[adults]"
                  component={renderStepper}
                  minValue="1"
                  increaseLabel="Increase adults"
                  decreaseLabel="Decrease adults"
                  {...this.props}
                />
              </div>
            </Option>
            <Option aria-label="Children">
              <Label>
                Children
                <div>{AGE_ANNOTATIONS.CHILD}</div>
              </Label>
              <div>
                <Field
                  name="booking[children]"
                  component={renderStepper}
                  increaseLabel="Increase children"
                  decreaseLabel="Decrease children"
                  {...this.props}
                />
              </div>
            </Option>
            <Footer>
              <CloseButton onClick={this.handleClose}>Close</CloseButton>
            </Footer>
          </Dropdown>
        </Panel>
        <Field name="booking[adults]" component="input" type="hidden" />
        <Field name="booking[children]" component="input" type="hidden" />
        <span>
          <ChevronDown />
        </span>
      </Wrapper>
    )
  }
}

GuestsInput.defaultProps = {
  guests: 1,
  capacity: 99,
}

export default GuestsInput
